import React, { createRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import useStyles from './UserTrackerList.styles';
import { loadUsers, selectListError, selectPaging, createUserNote, openCreateNote, closeCreateNote, updateUserProfile } from '../../state';
import MaterialTable, { Column, MaterialTableProps, Query } from 'material-table';
import { format } from 'date-fns';
import CreateNote from '../../components/CreateNote';
import { useSnackbar } from 'notistack';
import { UserSort } from '../../../../interfaces/user-sort';
import { useLastLocation } from 'react-router-last-location';

function formatDate(date: string) {
  const [year, month, day] = date.split('-');
  return `${month}/${day}/${year}`;
}

export default function UserTrackerList() {
  const error = useSelector(selectListError);
  const classes = useStyles();
  const dispatch = useDispatch();
  const paging = useSelector(selectPaging);
  const lastLocation = useLastLocation();
  const history = useHistory();
  const tableRef = createRef<MaterialTableProps<UserSort>>();
  const { enqueueSnackbar } = useSnackbar();

  if (error) {
    // TODO: implement correctly with retry operation
    return (
      <div className={classes.error}>
        {error}
      </div>
    )
  }

  const columns: Column<UserSort>[] = [
    { field: 'id', title: 'User ID', editable: 'never',
      render: (data: UserSort) => (<Link to={`/users/${data.id}`}>{data.id}</Link>)
    },
    { field: 'missingProfile', title: 'Missing Profile', editable: 'never', sorting: false, },
    { field: 'paidDate', title: 'Paid Date', width: 'auto', editable: 'never',
      render: (data: UserSort) => {
        const paidDate = data.paidDate;
        return paidDate !== '-' ? format(new Date(paidDate), 'M/d/yyyy, hh:mm aaa') : '';
      }
    },
    { field: 'subscriptionStartDate', title: 'Subscription Start Date', width: 'auto', editable: 'never',
      render: (data: UserSort) => {
        const subscriptionStartDate = data.subscriptionStartDate || '';
        return subscriptionStartDate !== '-' ? formatDate(subscriptionStartDate) : '';
      }
    },
    { field: 'subscriptionEndDate', title: 'Subscription End Date', width: 'auto', editable: 'never',
      render: (data: UserSort) => {
        const subscriptionEndDate = data.subscriptionEndDate || '';
        return subscriptionEndDate !== '-' ? formatDate(subscriptionEndDate) : '';
      }
    },
    { field: 'firstName', title: 'First Name', editable: 'never', },
    { field: 'lastName', title: 'Last Name', editable: 'never', },
    { field: 'nickname', title: 'Nickname', cellStyle: {minWidth: 240}, emptyValue: '...', sorting: false, },
    { field: 'sex', title: 'Sex', editable: 'never', sorting: false, },
    { field: 'product', title: 'Subscription level', editable: 'never', },
    { field: 'accessCode', title: 'Access Code', editable: 'never', sorting: false, },
    { field: 'group', title: 'Group', cellStyle: {minWidth: 240}, emptyValue: '...' },
    { field: '', title: 'Phone', width: 'auto', sorting: false, editable: 'never',
      render: (data: UserSort) => {
        const contactTelephone = data.contactTelephone;
        const {prefix = '', number = ''} = (contactTelephone || {});
        return prefix+number;
      }
    },
    { field: 'email', title: 'Email 1', editable: 'never', sorting: false, },
    { field: 'secondaryEmail', title: 'Email 2', cellStyle: {minWidth: 240}, emptyValue: '...', sorting: false, },
    { field: 'address.line1', title: 'Address Line 1', editable: 'never', sorting: false, },
    { field: 'address.line2', title: 'Address Line 2', editable: 'never', sorting: false, },
    { field: 'address.postcode', title: 'Postcode', editable: 'never', sorting: false, },
    { field: 'address.city', title: 'Town/City', editable: 'never', sorting: false, },
    { field: '', title: 'Country', editable: 'never', sorting: false, },
    { field: 'labRequestIds', title: 'Lab Requests', editable: 'never', sorting: false,
      render: (data: UserSort) => (
        <ul>
          {data.labRequestIds ? data.labRequestIds
            .split(',')
            .map((id) => (<li key={id}>
              <Link to={`/lab-requests/${id}`}>{id}</Link>
            </li>)) : null}
        </ul>
      )
    },
    { field: 'notesCount', title: 'Notes', cellStyle: {minWidth: 240}, editable: 'never', sorting: false,
      render: (data: UserSort) => {
        return (
          data.notesCount > 0 ?
            <div>
              <p>({data.notesCount}) {data.lastNote.text}</p>
              <p>{data.lastNote.createdBy} on {format(new Date(data.lastNote.createdAt), 'M/d/yyyy, hh:mm aaa')}</p>
            </div> :
            null
        )
      }
    },
  ];

  const currentSortColumn = columns.find(({field}) => field === paging.sortField);
  if (currentSortColumn) {
    currentSortColumn.defaultSort = paging.sortType;
  }

  const backFromDetails = lastLocation && lastLocation.pathname.includes('/users/');

  return (
    <div className="users">
      <MaterialTable
        title='User Tracker'
        columns={columns}
        tableRef={tableRef}
        data={(query) => {
          // backFromDetails is required to come around a bug with initialPage.
          const page = backFromDetails ? paging.page : query.page;
          return (dispatch(loadUsers({
            ...paging,
            page,
            pageSize: query.pageSize,
            sortField: query.orderBy.field,
            sortType: query.orderDirection,
            search: query.search
          })) as any)
            .then((data: any[]) => {
              if (backFromDetails) {
                history.push(`/user-tracker?page=${page}`);
              }
              return {
                data: data.map(o => ({ ...o })),
                page,
                totalCount: data.length === query.pageSize ? Infinity : (query.pageSize * page) + data.length
              };
            })
        }}
        cellEditable={{
          onCellEditApproved: (newValue, oldValue, rowData: any, columnDef) => {
            if (rowData.missingProfile) {
              enqueueSnackbar(`User has no profile`, {
                variant: 'error',
              });
              return Promise.resolve();
            }
            return (dispatch(updateUserProfile(rowData.id, {
              [columnDef.field as keyof UserSort]: newValue || ''
            })) as any)
            // Update locally to prevent the whole table re rendering.
            .then(() => rowData[columnDef.field as string] = newValue || null)
            .catch(() =>
              enqueueSnackbar(`Error updating ${columnDef.field as string}`, {
                variant: 'error',
              })
            );
          }
        }}
        actions={[
          {
            icon: 'addCircle',
            tooltip: 'Create new note',
            onClick: (event, user: any) => {
              dispatch(openCreateNote(user.id));
            }
          }
        ]}
        components={{
          Container: props => (
            <div style={{height: 'calc(100vh - 64px)'}}>
              {props.children}
            </div>
          ),
        }}
        localization={{
          pagination: {
            labelDisplayedRows: '{from}-{to}',
          },
        }}
        options={{
          headerStyle: { position: 'sticky', top: 0 },
          maxBodyHeight: 'calc(100vh - 128px - 53px)',
          paging: true,
          thirdSortClick: false,
          initialPage: 0,
          pageSize: paging.pageSize,
          searchText: paging.search,
          pageSizeOptions: [15, 30, 45],
          paginationType: 'normal',
          showFirstLastPageButtons: false,
          emptyRowsWhenPaging: false,
          debounceInterval: 500,
        }}
      />
      <CreateNote
        onClose={() => dispatch(closeCreateNote())}
        onSave={(text: string) => {
          (dispatch(createUserNote(text)) as any)
            .then(() => {
              if (tableRef?.current && tableRef?.current.onQueryChange) {
                tableRef?.current.onQueryChange({} as Query<UserSort>);
              }
            })
        }}
      />
    </div>
  );
}
